import React, { useState } from 'react';
import { Button, Input, message, Modal, Popconfirm, Space, Switch, Table, TableColumnsType, Tag } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  getAgentConfigList,
  deleteAgentConfig,
  toggleAgentConfig,
  copyAgentConfig,
  updateAgentConfig,
  AgentConfigVo,
} from '@/api/agent';
import { useUpdate } from '@/common/hook';
import EditAgentConfig from './editModal';
import AgentTestChatModal from './testChatModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const columns: TableColumnsType<AgentConfigVo> = [
  { title: '智能体编码', dataIndex: 'agentCode', key: 'agentCode', width: 150 },
  { title: '名称', dataIndex: 'name', key: 'name', width: 120 },
  { title: '描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 80,
    render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
  },
  {
    title: '推荐问题',
    dataIndex: 'recommendedQuestions',
    key: 'recommendedQuestions',
    width: 260,
    ellipsis: true,
    render: (recommendedQuestions?: string[]) => recommendedQuestions?.join('；') || '-',
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const AgentConfigList = () => {
  const [editData, setEditData] = useState<AgentConfigVo>();
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [updatingField, setUpdatingField] = useState<string>();
  const [update, updateFn] = useUpdate();

  // 测试对话状态
  const [testVisible, setTestVisible] = useState<boolean>(false);
  const [testAgent, setTestAgent] = useState<AgentConfigVo>();

  // 复制弹窗状态
  const [copyVisible, setCopyVisible] = useState<boolean>(false);
  const [copyData, setCopyData] = useState<AgentConfigVo>();
  const [copyCode, setCopyCode] = useState<string>('');
  const [copyName, setCopyName] = useState<string>('');

  const { data: agentList, loading } = useRequest(() => getAgentConfigList({ keyword }), {
    refreshDeps: [update, keyword],
  });

  const onDelete = (id: number) => {
    deleteAgentConfig(id).then(() => {
      message.success('删除成功');
      updateFn();
    });
  };

  const changeStatus = (record: AgentConfigVo) => {
    toggleAgentConfig({ id: record.id, status: record.status === 1 ? 0 : 1 }).then(updateFn);
  };

  const updateBooleanField = (
    record: AgentConfigVo,
    field: 'showInCard' | 'recordHistory',
    checked: boolean,
  ) => {
    const fieldKey = `${record.id}-${field}`;
    setUpdatingField(fieldKey);
    updateAgentConfig({ ...record, [field]: checked ? 1 : 0 })
      .then(() => {
        message.success('更新成功');
        updateFn();
      })
      .finally(() => setUpdatingField(undefined));
  };

  const handleCopy = () => {
    if (!copyCode) {
      message.warning('请输入新的智能体编码');
      return;
    }
    copyAgentConfig({ sourceId: copyData!.id, newAgentCode: copyCode, newName: copyName }).then(() => {
      message.success('复制成功');
      setCopyVisible(false);
      setCopyCode('');
      setCopyName('');
      updateFn();
    });
  };

  const buildColumns = [
    ...columns,
    {
      title: '卡片展示',
      dataIndex: 'showInCard',
      key: 'showInCard',
      width: 100,
      render: (showInCard: number | undefined, record: AgentConfigVo) => (
        <Switch
          checked={showInCard !== 0}
          loading={updatingField === `${record.id}-showInCard`}
          disabled={Boolean(updatingField) && updatingField !== `${record.id}-showInCard`}
          onChange={checked => updateBooleanField(record, 'showInCard', checked)}
        />
      ),
    },
    {
      title: '记录历史',
      dataIndex: 'recordHistory',
      key: 'recordHistory',
      width: 100,
      render: (recordHistory: number | undefined, record: AgentConfigVo) => (
        <Switch
          checked={recordHistory !== 0}
          loading={updatingField === `${record.id}-recordHistory`}
          disabled={Boolean(updatingField) && updatingField !== `${record.id}-recordHistory`}
          onChange={checked => updateBooleanField(record, 'recordHistory', checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'operation',
      width: 200,
      render: (_: any, record: AgentConfigVo) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setCopyData(record);
              setCopyCode(record.agentCode + '_copy');
              setCopyName(record.name + ' (副本)');
              setCopyVisible(true);
            }}
          >
            复制
          </Button>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setTestAgent(record);
              setTestVisible(true);
            }}
          >
            测试
          </Button>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => changeStatus(record)}>
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm title="确定删除该智能体？" onConfirm={() => onDelete(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增智能体
        </Button>
        <Input
          placeholder="搜索智能体名称/编码"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<AgentConfigVo>
        columns={buildColumns}
        dataSource={agentList ?? []}
        loading={loading}
        rowKey="id"
      />
      {editVisible && (
        <EditAgentConfig
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {copyVisible && (
        <Modal
          title="复制智能体"
          open={true}
          onCancel={() => setCopyVisible(false)}
          onOk={handleCopy}
        >
          <div style={{ marginBottom: 16 }}>
            <label>原智能体：</label>
            <span>{copyData?.name} ({copyData?.agentCode})</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label>新编码：</label>
            <Input value={copyCode} onChange={e => setCopyCode(e.target.value)} placeholder="请输入新的智能体编码" />
          </div>
          <div>
            <label>新名称：</label>
            <Input value={copyName} onChange={e => setCopyName(e.target.value)} placeholder="请输入新的智能体名称" />
          </div>
        </Modal>
      )}
      {testVisible && testAgent && (
        <AgentTestChatModal
          agent={testAgent}
          onCancel={() => {
            setTestVisible(false);
            setTestAgent(undefined);
          }}
        />
      )}
    </>
  );
};

export default AgentConfigList;
